<template>
  <form class="form-horizontal">
    <div
      v-bind:class="{ 'has-error': $v.gen[GEN_BUS].$invalid }"
      class="form-group"
    >
      <div class="col-4 col-sm-12">
        <label class="form-label" for="gen-bus">Bus Number</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <input
            v-model.number="gen[GEN_BUS]"
            class="form-input"
            id="gen-bus"
            inputmode="numeric"
          />
          <button
            v-on:click="navigateToBus"
            type="button"
            class="btn input-group-addon"
            aria-label="Navigate to bus"
          >
            <i v-if="busExists(gen[GEN_BUS])" class="icon icon-arrow-right"></i>
          </button>
        </div>
        <p v-if="!$v.gen[GEN_BUS].required" class="form-input-hint">
          Bus number is required.
        </p>
        <p v-else-if="!$v.gen[GEN_BUS].integer" class="form-input-hint">
          Bus number must be an integer.
        </p>
        <p v-else-if="!$v.gen[GEN_BUS].minValue" class="form-input-hint">
          Bus number must be >= 1.
        </p>
        <p v-else-if="!$v.gen[GEN_BUS].exists" class="form-input-hint">
          Bus {{ gen[GEN_BUS] }} must exist.
        </p>
      </div>
    </div>

    <div
      v-bind:class="{ 'has-error': $v.gen[GEN_STATUS].$invalid }"
      class="form-group"
    >
      <div class="col-4 col-sm-12">
        <label class="form-label" for="status">Status</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <label class="form-checkbox">
            <input
              v-model.number="gen[GEN_STATUS]"
              v-bind:true-value="1"
              v-bind:false-value="0"
              type="checkbox"
              id="status"
            />
            <i class="form-icon"></i> In Service
          </label>
        </div>
        <p v-if="!$v.gen[GEN_STATUS].between" class="form-input-hint">
          Status must be 0 or 1.
        </p>
      </div>
    </div>

    <div v-bind:class="{ 'has-error': $v.gen[PG].$invalid }" class="form-group">
      <div class="col-4 col-sm-12">
        <label class="form-label" for="pg">Active Power</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <span class="input-group-addon">P<sub>g</sub></span>
          <input
            v-model.number="gen[PG]"
            class="form-input"
            id="pg"
            inputmode="decimal"
          />
          <span class="input-group-addon">MW</span>
        </div>
        <p v-if="!$v.gen[PG].required" class="form-input-hint">
          Active power is required.
        </p>
        <p v-else-if="!$v.gen[PG].decimal" class="form-input-hint">
          Active power must be a decimal.
        </p>
      </div>
    </div>
    <div v-bind:class="{ 'has-error': $v.gen[QG].$invalid }" class="form-group">
      <div class="col-4 col-sm-12">
        <label class="form-label" for="qg">Reactive Power</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <span class="input-group-addon">Q<sub>g</sub></span>
          <input
            v-model.number="gen[QG]"
            class="form-input"
            id="qg"
            inputmode="decimal"
          />
          <span class="input-group-addon">MVAr</span>
        </div>
        <p v-if="!$v.gen[QG].required" class="form-input-hint">
          Reactive power is required.
        </p>
        <p v-else-if="!$v.gen[QG].decimal" class="form-input-hint">
          Reactive power must be a decimal.
        </p>
      </div>
    </div>

    <div v-bind:class="{ 'has-error': $v.gen[VG].$invalid }" class="form-group">
      <div class="col-4 col-sm-12">
        <label class="form-label" for="vg">Voltage Magnitude</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <span class="input-group-addon">V<sub>g</sub></span>
          <input
            v-model.number="gen[VG]"
            class="form-input"
            id="vg"
            inputmode="decimal"
          />
          <span class="input-group-addon">pu</span>
        </div>
        <p v-if="!$v.gen[VG].required" class="form-input-hint">
          Voltage magnitude is required.
        </p>
        <p v-else-if="!$v.gen[VG].decimal" class="form-input-hint">
          Voltage magnitude must be a decimal.
        </p>
        <p v-else-if="!$v.gen[VG].minValue" class="form-input-hint">
          Voltage magnitude must be >= 0.
        </p>
      </div>
    </div>

    <div v-bind:class="{ 'has-error': $v.gen[VG].$invalid }" class="form-group">
      <div class="col-4 col-sm-12">
        <label class="form-label" for="cost">Cost</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <span class="input-group-addon">C<sub>P</sub></span>
          <span v-html="cost(genCost)" class="form-input"></span>
          <router-link
            :to="{ name: 'genCost', params: { ord: ord } }"
            id="cost"
            class="btn input-group-addon"
            aria-label="Navigate to generator cost"
          >
            <i class="icon icon-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </div>

    <fieldset>
      <h6>Reactive Power Limits</h6>

      <div
        v-bind:class="{ 'has-error': $v.gen[QMAX].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="qmax">Maximum</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">Q<sub>max</sub></span>
            <input
              v-model.number="gen[QMAX]"
              class="form-input"
              id="qmax"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.gen[QMAX].required" class="form-input-hint">
            Maximum reactive power is required.
          </p>
          <p v-else-if="!$v.gen[QMAX].decimal" class="form-input-hint">
            Maximum reactive power must be a decimal.
          </p>
          <p v-else-if="!$v.gen[QMAX].minValue" class="form-input-hint">
            Maximum reactive power must be >= {{ gen[QMIN] }}.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.gen[QMIN].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="qmin">Minimum</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">Q<sub>min</sub></span>
            <input
              v-model.number="gen[QMIN]"
              class="form-input"
              id="qmin"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.gen[QMIN].required" class="form-input-hint">
            Minimum reactive power is required.
          </p>
          <p v-else-if="!$v.gen[QMIN].decimal" class="form-input-hint">
            Minimum reactive power must be a decimal.
          </p>
          <p v-else-if="!$v.gen[QMIN].maxValue" class="form-input-hint">
            Minimum reactive power must be &lt;= {{ gen[QMAX] }}.
          </p>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <h6>Active Power Limits</h6>

      <div
        v-bind:class="{ 'has-error': $v.gen[PMAX].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="pmax">Maximum</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">P<sub>max</sub></span>
            <input
              v-model.number="gen[PMAX]"
              class="form-input"
              id="pmax"
              inputmode="decimal"
            />
            <span class="input-group-addon">MW</span>
          </div>
          <p v-if="!$v.gen[PMAX].required" class="form-input-hint">
            Maximum active power is required.
          </p>
          <p v-else-if="!$v.gen[PMAX].decimal" class="form-input-hint">
            Maximum active power must be a decimal.
          </p>
          <p v-else-if="!$v.gen[PMAX].minValue" class="form-input-hint">
            Maximum active power must be >= {{ gen[PMIN] }}.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.gen[PMIN].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="pmin">Minimum</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">P<sub>min</sub></span>
            <input
              v-model.number="gen[PMIN]"
              class="form-input"
              id="pmin"
              inputmode="decimal"
            />
            <span class="input-group-addon">MW</span>
          </div>
          <p v-if="!$v.gen[PMIN].required" class="form-input-hint">
            Minimum active power is required.
          </p>
          <p v-else-if="!$v.gen[PMIN].decimal" class="form-input-hint">
            Minimum active power must be a decimal.
          </p>
          <p v-else-if="!$v.gen[PMIN].maxValue" class="form-input-hint">
            Minimum active power must be &lt;= {{ gen[PMAX] }}.
          </p>
        </div>
      </div>
    </fieldset>
  </form>
</template>

<script>
import {
  required,
  minValue,
  maxValue,
  integer,
  decimal,
  between
} from "vuelidate/lib/validators";

import MPCStore from "../../stores/MPCStore";
import GenCostTable from "./GenCostTable";

import {
  GEN_BUS,
  PG,
  QG,
  QMAX,
  QMIN,
  VG,
  GEN_STATUS,
  PMAX,
  PMIN
} from "../../stores/idx";

export default {
  name: "GenForm",
  props: ["ord"],
  data() {
    return {
      gen: MPCStore.methods.getGen(+this.ord - 1),
      genCost: MPCStore.methods.getGenCost(+this.ord - 1),
      GEN_BUS: GEN_BUS,
      PG: PG,
      QG: QG,
      QMAX: QMAX,
      QMIN: QMIN,
      VG: VG,
      GEN_STATUS: GEN_STATUS,
      PMAX: PMAX,
      PMIN: PMIN
    };
  },
  validations() {
    return {
      gen: {
        [GEN_BUS]: {
          required,
          integer,
          minValue: minValue(1),
          exists: this.busExists
        },
        [PG]: {
          required,
          decimal
        },
        [QG]: {
          required,
          decimal
        },
        [VG]: {
          required,
          decimal,
          minValue: minValue(0)
        },
        [GEN_STATUS]: {
          between: between(0, 1)
        },
        [QMAX]: {
          required,
          decimal,
          minValue: minValue(this.gen[QMIN])
        },
        [QMIN]: {
          required,
          decimal,
          maxValue: maxValue(this.gen[QMAX])
        },
        [PMAX]: {
          required,
          decimal,
          minValue: minValue(this.gen[PMIN])
        },
        [PMIN]: {
          required,
          decimal,
          maxValue: maxValue(this.gen[PMAX])
        }
      }
    };
  },
  methods: {
    navigateToBus() {
      let idx = MPCStore.methods.findBusIdx(this.gen[GEN_BUS]);
      this.$router.push({ name: "bus", params: { ord: idx + 1 } });
    },
    busExists: n => MPCStore.methods.findBusIdx(n) >= 0,
    cost: GenCostTable.methods.cost
  }
};
</script>

<style lang="scss" scoped>
@import "../../main.scss";

fieldset {
  margin-top: 1rem;
  border-top: 0.05rem solid $gray-color;
  padding-top: 0.5rem;
}
</style>
